/**
 * 做菜页：点菜 → 生成 → 结果 / 历史。
 *
 * 上下文摘要会显示出来。用户看到一道莫名其妙的菜时，能立刻确认
 * "哦，因为我设了不吃香菜"或"因为冰箱里有放了三天的菠菜"，
 * 而不是怀疑这功能在瞎编。
 */

import {
	DEFAULT_MEAL_BRIEF,
	longestStored,
	type DietProfile,
	type MealBrief,
	type Recipe,
} from '@fridge/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	ApiError,
	deleteRecipe,
	favoriteRecipe,
	fetchRecipes,
	pushMissingToBuyList,
} from '../api/client';
import { BriefPicker } from '../components/BriefPicker';
import { EmptyState, SkeletonRecipe } from '../components/Feedback';
import { GLYPHS, IconGlyph } from '../components/IconGlyph';
import { PageHeader } from '../components/PageHeader';
import { RecipeCard } from '../components/RecipeCard';
import { SegmentedControl } from '../components/SegmentedControl';
import { useToast } from '../components/Toast';
import { useGeneratingPulse } from '../motion';
import { useAppData } from '../state/app-context';

type CookTab = 'today' | 'history';

const DEFAULT_PROFILE: DietProfile = {
	goals: ['balanced'],
	avoid: [],
	spicy: 'mild',
	maxMinutes: null,
	updatedAt: '',
};

export function CookPage(): JSX.Element {
	const { fridge, prefs, refreshBuyList, generation, generate, replaceTodayRecipes } = useAppData();
	const toast = useToast();

	const [tab, setTab] = useState<CookTab>('today');
	const [brief, setBrief] = useState<MealBrief>(DEFAULT_MEAL_BRIEF);
	const [history, setHistory] = useState<Recipe[]>([]);
	const [historyLoaded, setHistoryLoaded] = useState(false);
	const [historyError, setHistoryError] = useState<string | null>(null);

	/**
	 * 「今天」这一栏和"生成中"都住在应用级 context 里，不在这个组件里。
	 * 因为路由用 `key={route}` 强制重挂载：用户点完生成、切去冰箱页再回来，
	 * 局部 state 会被清空，而那笔请求还在飞 —— 结果就成了没人接的东西。
	 */
	const { busy, recipes, contextSummary: summary } = generation;

	// 放得最久的三样，作为"优先用掉"的候选。不设阈值 —— 不判断新不新鲜，
	// 只是把放得久的摆到用户手边
	const aged = longestStored(fridge?.items ?? [], 3);
	const pulseRef = useGeneratingPulse<HTMLDivElement>(busy);

	// 用上次的点菜结果预填，熟手一点就走
	useEffect(() => {
		if (prefs) setBrief(prefs.lastBrief ?? prefs.defaultBrief ?? DEFAULT_MEAL_BRIEF);
	}, [prefs]);

	const loadHistory = useCallback(async () => {
		try {
			const { items } = await fetchRecipes(40);
			setHistory(items);
			setHistoryError(null);
		} catch (error) {
			// 不要把错误吞掉：之前这里静默失败，"历史永远为空"的 bug 藏了很久
			setHistoryError(error instanceof Error ? error.message : '历史加载失败');
		} finally {
			setHistoryLoaded(true);
		}
	}, []);

	useEffect(() => {
		void loadHistory();
	}, [loadHistory]);

	/**
	 * 只负责发起。成功/失败、提示条、待买清单刷新都在 context 里做 ——
	 * 这样即使这一页已经被卸载，那些副作用依然会完成。
	 *
	 * `void`：刻意不 await。发了就走，用户可以立刻切页面。
	 */
	function handleGenerate(): void {
		setTab('today');
		void generate(brief, 3);
	}

	/**
	 * 生成完之后把历史补上。
	 *
	 * 这个页面挂载时已经拉过一次历史，所以跳过第一次运行；
	 * 之后只在"这一批菜真的换了"时重拉，避免每次渲染都发请求。
	 */
	const seenBatch = useRef<string | null>(null);
	const firstRun = useRef(true);
	useEffect(() => {
		if (firstRun.current) {
			firstRun.current = false;
			return;
		}
		const ids = generation.recipes.map((item) => item.id).join(',');
		if (!ids || ids === seenBatch.current) return;
		seenBatch.current = ids;
		void loadHistory();
	}, [generation.recipes, loadHistory]);

	async function handleFavorite(recipe: Recipe): Promise<void> {
		try {
			const updated = await favoriteRecipe(recipe.id, !recipe.favorite);
			const apply = (list: Recipe[]): Recipe[] =>
				list.map((item) => (item.id === updated.id ? updated : item));
			replaceTodayRecipes(apply(recipes));
			setHistory(apply);
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '操作失败', 'error');
		}
	}

	async function handlePushMissing(recipe: Recipe): Promise<void> {
		try {
			const result = await pushMissingToBuyList(recipe.id);
			await refreshBuyList();
			toast.show(
				result.added > 0 ? `缺的 ${result.added} 样已加进待买。` : '这些材料都齐了。',
				result.added > 0 ? 'success' : 'info',
			);
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '加入失败', 'error');
		}
	}

	async function handleDelete(recipe: Recipe): Promise<void> {
		try {
			await deleteRecipe(recipe.id);
			replaceTodayRecipes(recipes.filter((item) => item.id !== recipe.id));
			setHistory((list) => list.filter((item) => item.id !== recipe.id));
			toast.show('删掉了。', 'info');
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '删除失败', 'error');
		}
	}

	const list = tab === 'today' ? recipes : history;

	return (
		<div ref={pulseRef}>
			<PageHeader title="做菜" subtitle="挑几样，让它替你决定今天吃什么" />

			<div className="px-4">
				<SegmentedControl<CookTab>
					options={[
						{ value: 'today', label: '今天' },
						{ value: 'history', label: '历史' },
					]}
					value={tab}
					onChange={setTab}
				/>
			</div>

			{tab === 'today' ? (
				<BriefPicker
					brief={brief}
					onChange={setBrief}
					profile={prefs?.profile ?? DEFAULT_PROFILE}
					agedItems={aged}
					busy={busy}
					onGenerate={handleGenerate}
				/>
			) : (
				<div className="h-4" />
			)}

			<div className="px-4">
				{/* 上下文摘要放在结果上方：解释了"为什么给我推这几道"，
				 * 而不是塞在页头或页尾让人对不上号 */}
				{!busy && summary && list.length > 0 ? (
					<p className="mb-3 flex items-start gap-1.5 rounded-input bg-surface-2 px-3 py-2 text-[11.5px] leading-relaxed text-label-2">
						<IconGlyph glyph={GLYPHS.info} size={13} className="mt-0.5 shrink-0" />
						{summary}
					</p>
				) : null}

				{busy ? <SkeletonRecipe count={2} /> : null}

				{!busy && tab === 'history' && !historyLoaded ? <SkeletonRecipe count={2} /> : null}

				{!busy && list.length === 0 ? (
					tab === 'today' ? (
						<EmptyState
							glyph={GLYPHS.sparkle}
							title="还没生成过菜谱"
							hint="上面那几行 chip 不用改也行，直接点「给我几个菜」。"
						/>
					) : (
						<EmptyState
							glyph={GLYPHS.cook}
							title={historyError ? '历史加载失败' : '历史是空的'}
							hint={
								historyError ??
								'生成过的菜谱都会留在这儿，随时能找回来。想长留的点一下星标就进收藏了。'
							}
						/>
					)
				) : null}

				<div className="flex flex-col gap-3">
					{list.map((recipe) => (
						<RecipeCard
							key={recipe.id}
							recipe={recipe}
							onToggleFavorite={(target) => void handleFavorite(target)}
							onPushMissing={(target) => void handlePushMissing(target)}
							onDelete={(target) => void handleDelete(target)}
						/>
					))}
				</div>
			</div>
		</div>
	);
}
